import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useWholesaleUserMutation } from "../store/api/AuthSlice";
import toast from "react-hot-toast";
import SEO from "./SEO";

// Validation Schema
const schema = Yup.object().shape({
  product: Yup.string().required("Product name is required"),
  quantity: Yup.number()
    .required("Quantity is required")
    .min(1, "Minimum quantity is 1"),
  fullName: Yup.string().required("Full name is required"),
  companyName: Yup.string().required("Company name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
  .required("Phone number is required")
  .matches(/^[0-9]{10}$/, "Phone number must be exactly 10 digits"),
  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
zip: Yup.string()
  .required("Postal/Zip code is required")
  .matches(/^[0-9]{5}$/, "Zip code must be exactly 5 digits"),
});

function Wholesale() {
const [wholesaleUser] = useWholesaleUserMutation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
  try {
    const res = await wholesaleUser(data).unwrap();
    toast.success(res?.message || "Form submitted successfully!");
    reset();
  } catch (err) {
    console.error(err);
    toast.error(err?.data?.message || "Form submission failed");
  }
};
   
 

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 bg-white shadow-2xl rounded-xl m-10">
      <SEO
  title="Wholesale Enquiry | Pufframa – Bulk Nicotine Product Orders"
  description="Submit a wholesale enquiry for bulk orders of nicotine products at Pufframa. Fast shipping, competitive pricing, and support for B2B nicotine partnerships."
  url="https://pufframa.com/wholesale"
  keywords="wholesale nicotine, bulk nicotine products, pufframa wholesale, B2B nicotine supplier, bulk order form"
  structuredData={{
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Wholesale Enquiry",
    description:
      "Pufframa wholesale form for businesses seeking bulk nicotine product orders. Contact us to partner for B2B nicotine supply.",
    url: "https://pufframa.com/wholesale"
  }}
/>

      <h2 className="text-3xl font-bold text-gray-800 text-center mb-8">
        Wholesale Enquiry Form
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Each field */}
        {[
          { id: "product", label: "Product", type: "text", placeholder: "Enter product name" },
          { id: "quantity", label: "Quantity", type: "number", placeholder: "Enter quantity" },
          { id: "fullName", label: "Full Name", type: "text", placeholder: "Enter your full name" },
          { id: "companyName", label: "Company Name", type: "text", placeholder: "Enter your company name" },
          { id: "email", label: "Email Address", type: "email", placeholder: "Enter your email" },
          { id: "phone", label: "Phone Number", type: "text", placeholder: "Enter your phone number" },
        ].map(({ id, label, type, placeholder }) => (
          <div key={id}>
            <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
            <input
              {...register(id)}
              type={type}
              placeholder={placeholder}
              className={`w-full px-4 py-2 border ${
                errors[id] ? "border-red-500" : "border-gray-300"
              } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors[id] && <p className="text-red-500 text-sm mt-1">{errors[id]?.message}</p>}
          </div>
        ))}

        {/* Address - Full width */}
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Address</label>
          <input
            {...register("address")}
            placeholder="Enter full address"
            className={`w-full px-4 py-2 border ${
              errors.address ? "border-red-500" : "border-gray-300"
            } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address.message}</p>}
        </div>

        {/* City and State */}
        {[
          { id: "city", label: "City", placeholder: "Enter your city" },
          { id: "state", label: "State", placeholder: "Enter your state" },
        ].map(({ id, label, placeholder }) => (
          <div key={id}>
            <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
            <input
              {...register(id)}
              placeholder={placeholder}
              className={`w-full px-4 py-2 border ${
                errors[id] ? "border-red-500" : "border-gray-300"
              } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors[id] && <p className="text-red-500 text-sm mt-1">{errors[id]?.message}</p>}
          </div>
        ))}

        {/* Zip - Full Width */}
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Postal/Zip Code</label>
          <input
            {...register("zip")}
            placeholder="Enter your postal/zip code"
            className={`w-full px-4 py-2 border ${
              errors.zip ? "border-red-500" : "border-gray-300"
            } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          {errors.zip && <p className="text-red-500 text-sm mt-1">{errors.zip.message}</p>}
        </div>

        {/* Submit Button */}
        <div className="md:col-span-2">
          <button
            type="submit"
            className="w-full py-3 bg-[#10B981] text-white font-semibold rounded-md hover:bg-blue-700 transition duration-300"
          >
            Submit Enquiry
          </button>
        </div>
      </form>
    </div>
  );
}

export default Wholesale;
